import { CompanyVendorFile } from 'src/app/classes/models/company-vendor-file';
import { InsuranceCertificateStatus } from 'src/app/interfaces/insurance-certificate-status';
import { ModelDataType } from 'src/app/interfaces/model-data-type';
import { InsuranceAgent } from 'src/app/interfaces/models/insurance-agent';

import { BaseModel } from './base-model';
import { InsurancePolicy } from './insurance-policy';
import { SubNationalDivision } from './sub-national-division';

export class InsuranceCertificate extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static InsuranceCertificateProperties: (
    | {
        property: keyof InsuranceCertificate;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof InsuranceCertificate;
        type: 'alias';
      }
    | {
        property: keyof InsuranceCertificate;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'CompanyVendorId', type: 'column', dataType: 'number' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    { property: 'InsuranceAgentId', type: 'column', dataType: 'number' },
    { property: 'ancestryId', type: 'column', dataType: 'string' },
    { property: 'isCertAndSigned', type: 'column', dataType: 'boolean' },
    { property: 'viveNamedCertHolder', type: 'column', dataType: 'boolean' },
    { property: 'agentSigned', type: 'column', dataType: 'boolean' },
    { property: 'isLegalNameVerified', type: 'column', dataType: 'boolean' },
    { property: 'desOfOps', type: 'column', dataType: 'boolean' },
    { property: 'status', type: 'column', dataType: 'string' },
    { property: 'statusDate', type: 'column', dataType: 'date' },
    { property: 'prodName', type: 'column', dataType: 'string' },
    { property: 'prodAddress', type: 'column', dataType: 'string' },
    { property: 'prodAddress2', type: 'column', dataType: 'string' },
    { property: 'prodCity', type: 'column', dataType: 'string' },
    { property: 'prodState', type: 'column', dataType: 'string' },
    { property: 'prodZip', type: 'column', dataType: 'string' },
    { property: 'prodPhone', type: 'column', dataType: 'string' },
    { property: 'prodPhoneExt', type: 'column', dataType: 'string' },
    { property: 'prodEmail', type: 'column', dataType: 'string' },
    { property: 'contactName', type: 'column', dataType: 'string' },
    { property: 'contactPhone', type: 'column', dataType: 'string' },
    { property: 'contactFax', type: 'column', dataType: 'string' },
    { property: 'contactEmail', type: 'column', dataType: 'string' },
    { property: 'contactAddress', type: 'column', dataType: 'string' },
    { property: 'contactCity', type: 'column', dataType: 'string' },
    { property: 'contactState', type: 'column', dataType: 'string' },
    { property: 'contactZip', type: 'column', dataType: 'string' },
    { property: 'agentFirstName', type: 'column', dataType: 'string' },
    { property: 'agentLastName', type: 'column', dataType: 'string' },
    { property: 'agentCompany', type: 'column', dataType: 'string' },
    { property: 'agentPhone', type: 'column', dataType: 'string' },
    { property: 'agentPhoneExt', type: 'column', dataType: 'string' },
    { property: 'agentAddress', type: 'column', dataType: 'string' },
    { property: 'agentAddress2', type: 'column', dataType: 'string' },
    { property: 'agentCity', type: 'column', dataType: 'string' },
    { property: 'agentState', type: 'column', dataType: 'string' },
    { property: 'agentZip', type: 'column', dataType: 'string' },
    { property: 'agentEmail', type: 'column', dataType: 'string' },
    { property: 'isPaused', type: 'column', dataType: 'boolean' },
    { property: 'pauseDate', type: 'column', dataType: 'date' },
    { property: 'pauseReason', type: 'column', dataType: 'string' },
    { property: 'wasMigrated', type: 'column', dataType: 'boolean' },
    { property: 'rejectedReason', type: 'column', dataType: 'string' },
    { property: 'scanJson', type: 'column', dataType: 'JSON' },
    {
      property: 'acceptNonRatedAMBestRating',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },

    { property: 'nextExpiringInsurancePolicyDate', type: 'alias' },
    { property: 'policyTypes', type: 'alias' },
    { property: 'policyNumbers', type: 'alias' },
    { property: 'policyExpirationDates', type: 'alias' },

    {
      property: 'InsurancePolicies',
      model: 'InsurancePolicy',
      type: 'association'
    },
    {
      property: 'InsuranceAgent',
      model: 'InsuranceAgent',
      type: 'association'
    },
    {
      property: 'CompanyVendorFiles',
      model: 'CompanyVendorFile',
      type: 'association'
    },
    { property: 'ContactState', model: 'ContactState', type: 'association' }
  ];

  static {
    InsuranceCertificate.InsuranceCertificateProperties.forEach(
      (propertyObject) => {
        const { property, type } = propertyObject;
        if (type === 'column') {
          InsuranceCertificate.columnMap.set(property, propertyObject);
        }
        if (type === 'alias') {
          InsuranceCertificate.aliasedColumnMap.set(
            property,
            propertyObject as { property: string; type: 'alias' }
          );
        }
        if (type === 'association') {
          InsuranceCertificate.associationMap.set(
            property,
            propertyObject as {
              property: string;
              type: 'association';
              model: string;
            }
          );
        }
      }
    );
  }

  constructor(InsuranceCertificateObject: Partial<InsuranceCertificate>) {
    super();

    InsuranceCertificate.InsuranceCertificateProperties.forEach(
      (propertyObject) => {
        const { property } = propertyObject;
        const value = InsuranceCertificateObject[property];
        if (value !== undefined) {
          Object.assign(this, { [property]: value });
        }
      }
    );
  }

  id?: number;
  CompanyVendorId?: number;
  CompanyId?: number;
  VendorId?: number;
  InsuranceAgentId?: number;
  ancestryId?: string;
  isCertAndSigned?: boolean;
  viveNamedCertHolder?: boolean;
  agentSigned?: boolean;
  isLegalNameVerified?: boolean;
  desOfOps?: boolean;
  status?: InsuranceCertificateStatus;
  statusDate?: Date;
  prodName?: string | null;
  prodAddress?: string | null;
  prodAddress2?: string | null;
  prodCity?: string | null;
  prodState?: string | null;
  prodZip?: string | null;
  prodPhone?: string | null;
  prodPhoneExt?: string | null;
  prodEmail?: string | null;
  contactName?: string;
  contactPhone?: string;
  contactFax?: string | null;
  contactEmail?: string;
  contactAddress?: string | null;
  contactCity?: string | null;
  contactState?: string | null;
  contactZip?: string | null;
  agentFirstName?: string;
  agentLastName?: string;
  agentCompany?: string | null;
  agentPhone?: string | null;
  agentPhoneExt?: string | null;
  agentAddress?: string | null;
  agentAddress2?: string | null;
  agentCity?: string | null;
  agentState?: string | null;
  agentZip?: string | null;
  agentEmail?: string;
  isPaused?: boolean;
  pauseDate?: Date | null;
  pauseReason?: string | null;
  wasMigrated?: boolean;
  rejectedReason?: string | null;
  scanJson?: any | null;
  acceptNonRatedAMBestRating?: boolean;
  createdAt?: Date;
  updatedAt?: Date;

  // aliased columns
  nextExpiringInsurancePolicyDate?: Date;
  policyTypes?: string;
  policyNumbers?: string;
  policyExpirationDates?: string;

  InsurancePolicies?: InsurancePolicy[];
  GLInsurancePolicy?: InsurancePolicy;
  AUTOInsurancePolicy?: InsurancePolicy;
  UMBInsurancePolicy?: InsurancePolicy;
  WCInsurancePolicy?: InsurancePolicy;
  PLInsurancePolicy?: InsurancePolicy;
  InsuranceAgent?: InsuranceAgent;
  CompanyVendorFiles?: CompanyVendorFile[];
  ContactState?: SubNationalDivision;
}
